import React, { useMemo } from 'react';
import { Button, Card, Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { faCommentDots, faExclamationTriangle, faThumbsUp, faPersonBooth } from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { urlConst } from '../../../consts';
import { ReqStatus } from './ReqStatus';
import { HashLink } from 'react-router-hash-link';
import { isCommentReaded, isNeedExtData } from '../../../util';

function renderExtDataButton(request) {
    if (!request || request.status.id !== 3) return null;
    if (isNeedExtData(request))
        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tooltip-extdata-req-${request.num}`}>Заполнить доп данные</Tooltip>}
            >
                <Button
                    as={HashLink}
                    to={`${request.num}#extData`}
                    variant="link"
                    className="icon-button"
                    size="sm"
                    smooth
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} size="1x" beatFade />
                </Button>
            </OverlayTrigger>
        );
    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-extdata-req-${request.num}`}>Доп данные</Tooltip>}
        >
            <Button
                as={HashLink}
                to={`${request.num}#extData`}
                variant="link"
                className="icon-button text-info"
                size="sm"
                smooth
            >
                <FontAwesomeIcon icon={faThumbsUp} size="1x" />
            </Button>
        </OverlayTrigger>
    );
}

function NewCommentsButton({ request }) {
    const selfId = useSelector((state) => state.auth && state.auth.user && state.auth.user.id);
    const hasNewComments = useMemo(() => {
        if (!request) return false;
        return request.comments.some((comment) => !isCommentReaded(comment, selfId));
    }, [request, selfId]);
    return (
        <>
            {hasNewComments && (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-comments-req-${request.num}`}>Есть новые комментарии</Tooltip>}
                >
                    <Button
                        as={HashLink}
                        to={`${request.num}#comments`}
                        variant="link"
                        className="icon-button text-info"
                        size="sm"
                        smooth
                    >
                        <FontAwesomeIcon icon={faCommentDots} size="1x" beatFade />
                    </Button>
                </OverlayTrigger>
            )}
        </>
    );
}

function RequestRoomButton({ request }) {
    return (
        <>
            {request && request.room && (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-room-req-${request.num}`}>Определена гримерка!</Tooltip>}
                >
                    <Button
                        as={Link}
                        to={`${request.num}/room`}
                        variant="link"
                        className="icon-button text-info"
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faPersonBooth} size="1x" />
                    </Button>
                </OverlayTrigger>
            )}
        </>
    );
}

function RequestsTable(props) {
    const { title, noRequestsText, requests } = props;
    const sortedRequests = useMemo(() => {
        if (!requests) return [];
        return [...requests].sort((a, b) => a.num - b.num);
    }, [requests]);
    return (
        <Card className="mx-auto mb-4">
            <Card.Header>
                <Card.Title>{title}</Card.Title>
            </Card.Header>
            <Card.Body>
                {sortedRequests.length ? (
                    <>
                        <Row className="d-none d-sm-flex mb-2 font-weight-bold">
                            <Col sm={1}>ID</Col>
                            <Col sm={3}>Номинация</Col>
                            <Col sm={3}>Описание</Col>
                            <Col sm={3}>Статус</Col>
                            <Col sm={2} />
                        </Row>
                        {sortedRequests.map((request) => (
                            <Row className="mb-2 pt-2 border-top" key={request.id}>
                                <Col xs={2} sm={1}>
                                    {request.num}
                                </Col>
                                <Col xs={10} sm={3}>
                                    {request.nomination && request.nomination.name}
                                </Col>
                                <Col xs={12} sm={3}>
                                    {request.title}
                                </Col>
                                <Col xs={12} sm={3}>
                                    <ReqStatus status={request.status} />
                                </Col>
                                <Col xs={12} sm={2}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`tooltip-open-req-${request.num}`}>Открыть заявку</Tooltip>
                                        }
                                    >
                                        <Button
                                            as={Link}
                                            to={`${request.num}`}
                                            variant="link"
                                            className="icon-button"
                                            size="sm"
                                        >
                                            <FontAwesomeIcon icon={faEye} size="1x" />
                                        </Button>
                                    </OverlayTrigger>
                                    {renderExtDataButton(request)}
                                    <NewCommentsButton request={request} />
                                    <RequestRoomButton request={request} />
                                </Col>
                            </Row>
                        ))}
                    </>
                ) : (
                    <b>{noRequestsText}</b>
                )}
            </Card.Body>
        </Card>
    );
}

function OldEvents(props) {
    const { events } = props;
    return (
        (events && events.length > 0 && (
            <Card className="mx-auto my-5">
                <Card.Body>
                    <h5 className="text-muted">Архив событий:</h5>
                    <ul>
                        {events.map((event) => (
                            <li key={event.id}>
                                <Link className="text-muted" to={`/event/${event.name}/${urlConst.REQUESTS}`}>
                                    {event.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Card.Body>
            </Card>
        )) ||
        null
    );
}

function RequestListPage() {
    const { eventName } = useParams();
    const { requests, events, currentEvent } = useSelector((state) => ({
        requests: state.requests.requests,
        events: state.events.events,
        currentEvent: state.events.currentEvent,
    }));
    const { author, participant } = requests || {};
    const eventTitle = useMemo(() => {
        if (!eventName) return '';
        if (currentEvent && eventName === currentEvent.name) return currentEvent.title;
        if (events) {
            const event = events.find((ev) => ev.name === eventName);
            if (event) return event.title;
        }
        return '';
    }, [events, eventName, currentEvent]);

    return (
        <Container className="py-5">
            {eventTitle && <h2 className="mb-3">{eventTitle}</h2>}
            <RequestsTable
                title="Созданные мной заявки"
                requests={author}
                noRequestsText="Вами пока не создано заявок"
            />
            <RequestsTable
                title="Участие в чужих заявках"
                requests={participant}
                noRequestsText="В чужих заявках Вы не заявлены"
            />
            {!eventTitle && <OldEvents events={events} />}
        </Container>
    );
}

export default RequestListPage;
